import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, TextField, Typography, Checkbox, FormControlLabel, Switch } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import { createStaff, updateStaff, checkPhoneNumberDuplicate, checkUsernameDuplicate } from '../../api/staff';
import { listBranchAccessGroups } from '../../api/branch-access-group';
import { centersDetail } from '../../api/store-management';
import AnimationPage from '../../pages/AnimationPage';
import { Respo, employeeClassification, gender, employeeLoginYn } from '../../constants/constants';
import { FormInputDate } from '../../form-components/FormInputDate';
import { FormInputPhone } from '../../form-components/FormInputPhone';
import { FormInputRadio } from '../../form-components/FormInputRadio';
import { FormInputText } from '../../form-components/FormInputText';
import { FormInputTextArea } from '../../form-components/FormInputTextArea';
import { FormInputDropdown } from '../form/FormInputDropdown';
import { getEmployeePositionsList } from '../../api/employeePosition';
import { errorMessages, validateDateRange } from '../../utils/methods';
import { RHFCheckbox } from '../CheckBox';

const qs = require('qs');

const NO_KOREAN_PATTERN = /^[^ㄱ-ㅎㅏ-ㅣ가-힣]*$/;
// 주 소정근로시간 입력: 정수 또는 소수 첫째 자리까지만 허용 (예: 40, 37.5)
const WEEKLY_HOURS_PATTERN = /^\d*\.?\d?$/;
// 주 15시간 미만은 주휴수당 대상이 아님 (BE SalarySettlementService.java:1105 판정 기준과 동일)
const WEEKLY_HOLIDAY_ELIGIBLE_HOURS = 15;

// 주 소정근로시간 → 월 소정근로시간 안내 문구 (저장하지 않고 항상 화면에서 즉시 계산)
// 공식: round((주소정 + 주휴) × 365 ÷ 7 ÷ 12), 반올림은 마지막에 한 번만 (HALF_UP)
// 검산: 40→209, 37.5→196, 20→104, 15→78, 14→61
function getMonthlyHoursGuide(weeklyHours) {
  const weekly = Number(weeklyHours);
  if (!weekly || weekly <= 0) return '';
  // 주 15시간 미만은 주휴 대상이 아니라 환산에서 주휴가 빠진다.
  // 그 경우까지 "주휴 포함"이라고 적으면 왜 시간이 적게 나오는지 알 수 없다.
  const eligible = weekly >= WEEKLY_HOLIDAY_ELIGIBLE_HOURS;
  const weeklyHoliday = eligible ? weekly / 5 : 0;
  const monthly = Math.round(((weekly + weeklyHoliday) * 365) / 7 / 12);
  const basis = eligible
    ? '주휴 포함 기준'
    : `주휴 미적용 (주 ${WEEKLY_HOLIDAY_ELIGIBLE_HOURS}시간 미만)`;
  return `월 ${monthly}시간 · ${basis}`;
}

// ============================================
// Layout
// ============================================
const PageWrapper = styled(Box)({ display: 'flex', gap: '28px', alignItems: 'flex-start' });

// ============================================
// Left: Profile + Basic Info
// ============================================
const LeftCard = styled(Box)({
  width: '420px', minWidth: '420px', backgroundColor: '#fff',
  borderRadius: '20px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
  overflow: 'hidden', position: 'sticky', top: '24px',
});

const ProfileHeader = styled(Box)({
  background: 'linear-gradient(135deg, #4ab3bc 0%, #3a9da5 100%)',
  padding: '32px 28px 52px', display: 'flex', alignItems: 'center', gap: '20px',
});

const PhotoWrap = styled(Box)({ position: 'relative', flexShrink: 0 });

const Photo = styled('img')({
  width: '88px', height: '88px', borderRadius: '50%', objectFit: 'cover',
  border: '3px solid rgba(255,255,255,0.4)',
});

const CameraBtn = styled('label')({
  position: 'absolute', bottom: '0', right: '-2px', width: '30px', height: '30px',
  borderRadius: '50%', backgroundColor: '#fff', display: 'flex', alignItems: 'center',
  justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
  transition: 'transform 0.15s', '&:hover': { transform: 'scale(1.1)' },
});

const FormBody = styled(Box)({
  padding: '28px', marginTop: '-24px', backgroundColor: '#fff',
  borderRadius: '20px 20px 0 0', position: 'relative',
  display: 'flex', flexDirection: 'column', gap: '18px',
});

const FieldGroup = styled(Box)({ display: 'flex', flexDirection: 'column', gap: '6px' });

const FieldLabel = styled(Typography)({
  fontSize: '13px', fontWeight: 600, color: '#737373', letterSpacing: '-0.01em',
  display: 'flex', alignItems: 'center', gap: '6px',
});

const RequiredMark = styled('span')({
  color: '#f43f5e', fontSize: '14px', fontWeight: 700, lineHeight: 1,
});

// ============================================
// Right
// ============================================
const RightPanel = styled(Box)({ flex: 1, display: 'flex', flexDirection: 'column', gap: '20px', minWidth: 0 });

const Section = styled(Box)({
  backgroundColor: '#fff', borderRadius: '20px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)', padding: '28px 32px',
});

const SectionTitle = styled(Typography)({
  fontSize: '17px', fontWeight: 700, color: '#262626', marginBottom: '20px',
  display: 'flex', alignItems: 'center', gap: '8px',
  '&::before': { content: '""', width: '4px', height: '18px', borderRadius: '2px', backgroundColor: '#4ab3bc', display: 'inline-block' },
});

const Row = styled(Box)({
  display: 'flex', alignItems: 'center', minHeight: '48px', padding: '8px 0',
  '& + &': { borderTop: '1px solid #f5f5f5' },
});

const RowLabel = styled(Typography)({ width: '130px', minWidth: '130px', fontSize: '14px', fontWeight: 600, color: '#525252', display: 'flex', alignItems: 'center', gap: '4px' });
const RowContent = styled(Box)({ flex: 1, display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 });

const TealSwitch = styled(Switch)({
  width: '44px', height: '24px', padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: '3px',
    '&.Mui-checked': { transform: 'translateX(20px)', color: '#fff', '& + .MuiSwitch-track': { backgroundColor: '#4ab3bc', opacity: 1 } },
  },
  '& .MuiSwitch-thumb': { width: '18px', height: '18px', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' },
  '& .MuiSwitch-track': { borderRadius: '12px', backgroundColor: '#d4d4d4', opacity: 1 },
});

const Footer = styled(Box)({
  display: 'flex', justifyContent: 'flex-end', gap: '12px', backgroundColor: '#fff',
  borderRadius: '20px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)', padding: '16px 32px',
});

const CancelBtn = styled(Button)({
  minWidth: '120px', height: '44px', borderRadius: '12px', fontSize: '15px', fontWeight: 600,
  backgroundColor: '#f5f5f5', color: '#737373', textTransform: 'none', '&:hover': { backgroundColor: '#e5e5e5' },
});

const SubmitBtn = styled(Button)({
  minWidth: '160px', height: '44px', borderRadius: '12px', fontSize: '15px', fontWeight: 600,
  background: 'linear-gradient(135deg, #4ab3bc 0%, #3a9da5 100%)', color: '#fff', textTransform: 'none',
  boxShadow: '0 2px 8px rgba(74,179,188,0.3)',
  '&:hover': { background: 'linear-gradient(135deg, #3a9da5 0%, #2e8a92 100%)' },
  '&:disabled': { background: '#d4d4d4', color: '#fff', boxShadow: 'none' },
});

const BackLink = styled(Link)({
  display: 'inline-flex', alignItems: 'center', gap: '8px', textDecoration: 'none',
  color: '#525252', marginBottom: '20px', transition: 'color 0.15s', '&:hover': { color: '#262626' },
});

const RADIO_TEAL = { color: '#d4d4d4', '&.Mui-checked': { color: '#4ab3bc' } };

const HintText = styled(Typography)({
  fontSize: '12px', fontWeight: 500, color: '#f43f5e', marginTop: '4px',
});

const HelperText = styled(Typography)({
  fontSize: '12px', fontWeight: 500, color: '#a3a3a3', marginTop: '4px',
});

const StyledCheckbox = styled(Checkbox)({ padding: '0', marginRight: '4px' });
const AllCheckboxLabel = styled(FormControlLabel)({ marginRight: '0', marginLeft: '0', '& .MuiFormControlLabel-label': { fontSize: '14px', whiteSpace: 'nowrap' } });
const FilterDivider = styled('span')({ display: 'inline-block', width: '1px', height: '18px', backgroundColor: '#e5e5e5', margin: '0 20px' });

const INPUT_SX = {
  width: '100%',
  '& .MuiInputBase-root': { backgroundColor: '#fafafa', borderRadius: '10px', fontSize: '14px', '&:hover': { backgroundColor: '#f5f5f5' }, '&.Mui-focused': { backgroundColor: '#fff' } },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e5e5' },
};

const DATE_INPUT_SX = {
  backgroundColor: '#fafafa', borderRadius: '10px', width: '100%',
  '.MuiInputBase-root': { paddingRight: '0px', fontSize: '14px' },
  '.MuiIconButton-root': { marginRight: '0px' },
  input: { paddingRight: '0px!important' },
  '& .MuiSvgIcon-root': { fill: '#4AB3BC !important' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e5e5' },
};

// ============================================
// Component
// ============================================
function EmployeeTab1({ employ, userBranch }) {
  const toast = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const user = useSelector(state => state.user.user);
  const token = useSelector(state => state.user.user.token);
  const userHead = useSelector(state => state.user.user.headBranch);
  const [loader, setLoader] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [positions, setPositions] = useState([]);
  const [sportType, setSportType] = useState([]);
  const [phoneChecked, setPhoneChecked] = useState(false);
  const [phoneChecking, setPhoneChecking] = useState(false);
  const [usernameChecked, setUsernameChecked] = useState(false);
  const [usernameChecking, setUsernameChecking] = useState(false);

  const isEditMode = !!employ;
  const { handleSubmit, control, setValue, setError, clearErrors, trigger, reset, getValues } = useForm();

  const watchSportTypes = useWatch({ control, name: 'sportTypes' });
  const watchClassification = useWatch({ control, name: 'classification' });
  const watchHiredDate = useWatch({ control, name: 'hiredDate' });
  const watchName = useWatch({ control, name: 'name' });
  const watchPassword = useWatch({ control, name: 'password' });
  const watchPhone = useWatch({ control, name: 'phoneNumber' });
  const watchEmail = useWatch({ control, name: 'email' });
  const watchContractWeeklyHours = useWatch({ control, name: 'contractWeeklyHours' });
  const monthlyHoursGuide = getMonthlyHoursGuide(watchContractWeeklyHours);

  // 실시간 형식 체크
  const passwordError = watchPassword && watchPassword.trim() && !/^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*-])[A-Za-z\d!@#$%^&*-]{8,16}$/.test(watchPassword)
    ? '영문, 숫자, 특수문자를 포함해 8~16자로 입력해주세요' : '';
  const phoneError = watchPhone && watchPhone.trim() && !/^010-\d{4}-\d{4}$/.test(watchPhone)
    ? '010-0000-0000 형식으로 입력해주세요' : '';
  const emailError = watchEmail && watchEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(watchEmail)
    ? '올바른 이메일 형식으로 입력해주세요' : '';

  useEffect(() => {
    if (watchClassification === 'WORKING') setValue('leftDate', null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchClassification]);

  const accessGroupsQuery = useQuery({
    queryKey: ['branch-access-groups', userBranch?.branchId],
    queryFn: () => listBranchAccessGroups(userBranch.branchId, token),
    enabled: isEditMode && !!userBranch?.branchId && !!token,
    staleTime: 5 * 60 * 1000,
    select: groups => {
      const byCode = new Map();
      (groups ?? []).forEach(g => {
        const entry = byCode.get(g.accessGroupCode) ?? { groupName: '', sportTypes: [] };
        if (!entry.groupName && g.groupName) entry.groupName = g.groupName;
        if (g.sportType && !entry.sportTypes.includes(g.sportType)) entry.sportTypes.push(g.sportType);
        byCode.set(g.accessGroupCode, entry);
      });
      return [...byCode.entries()].map(([code, entry]) => ({
        value: code,
        label: entry.groupName || entry.sportTypes.map(s => t(s)).join('·') || code,
      }));
    },
  });
  const accessGroupOptions = accessGroupsQuery.data ?? [];

  const handleAllCheck = fieldName => {
    const currentValues = getValues(fieldName) || [];
    setValue(fieldName, currentValues.length === sportType.length ? [] : sportType.map(opt => opt.value));
  };

  const handleUploadImage = e => setPreviewImage(e.target.files[0]);

  const getProfileImageSrc = () => {
    if (previewImage) return (window.URL || window.webkitURL).createObjectURL(previewImage);
    if (employ?.authImage) return employ.authImage.startsWith('data:') ? employ.authImage : `data:image/jpeg;base64,${employ.authImage}`;
    return employ?.userImgURL ?? '/images/profile@2x.png';
  };

  const handleCheckUsername = async () => {
    const username = getValues('username');
    if (!username || username.trim() === '') {
      toast.error('아이디를 입력해주세요');
      focusField('username');
      return;
    }
    if (!NO_KOREAN_PATTERN.test(username)) {
      toast.error('아이디에 한글은 사용할 수 없습니다');
      focusField('username');
      return;
    }
    try {
      setUsernameChecking(true);
      const result = await checkUsernameDuplicate(username, token);
      if (result.duplicate) {
        toast.error('이미 사용 중인 아이디입니다');
        setUsernameChecked(false);
      } else {
        toast.success('사용 가능한 아이디입니다');
        setUsernameChecked(true);
      }
    } catch {
      toast.error('중복 확인에 실패했습니다');
    } finally {
      setUsernameChecking(false);
    }
  };

  const handleCheckPhone = async () => {
    const phone = getValues('phoneNumber');
    if (!phone || phone.trim() === '') {
      toast.error('휴대폰 번호를 입력해주세요');
      focusField('phoneNumber');
      return;
    }
    if (!/^010-\d{4}-\d{4}$/.test(phone)) {
      toast.error('올바른 휴대폰 번호 형식이 아닙니다 (010-0000-0000)');
      focusField('phoneNumber');
      return;
    }
    try {
      setPhoneChecking(true);
      const result = await checkPhoneNumberDuplicate(phone, token);
      if (result.duplicate) {
        toast.error('이미 등록된 휴대폰 번호입니다');
        setPhoneChecked(false);
      } else {
        toast.success('사용 가능한 휴대폰 번호입니다');
        setPhoneChecked(true);
      }
    } catch {
      toast.error('중복 확인에 실패했습니다');
    } finally {
      setPhoneChecking(false);
    }
  };

  const fetchPositions = async () => {
    try {
      const queryParams = { headBranchId: userHead?.headBranchId, branchId: userBranch?.branchId };
      const result = await getEmployeePositionsList(token, qs.stringify(queryParams));
      setPositions([
        { value: '', label: '직급을 선택해주세요' },
        ...result.content.map(p => ({ value: p.employeePositionId, label: p.positionName || p.employeePositionName })),
      ]);
    } catch (error) {
      console.error('직급 목록 조회 오류:', error);
      toast.error('직급 목록을 불러오는데 실패했습니다');
    }
  };

  const fetchSportTypes = async () => {
    try {
      const response = await centersDetail(token, userBranch?.centerId);
      setSportType((response?.branchDetailInfo?.sportTypes || []).map(type => ({ label: type, value: type })));
    } catch (error) {
      console.error('종목 목록 조회 오류:', error);
    }
  };

  useEffect(() => {
    if (userHead?.headBranchId && userBranch?.branchId) fetchPositions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userHead, userBranch, token]);

  useEffect(() => {
    if (token && userBranch?.centerId) fetchSportTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userBranch?.centerId]);

  // 필수값 검증 순서 (첫 번째 에러만 표시)
  // createOnly: 생성 모드에서만 필수
  const REQUIRED_FIELDS = [
    { name: 'name', label: '이름을 입력해주세요', createOnly: true },
    { name: 'username', label: '아이디를 입력해주세요', createOnly: true },
    { name: 'phoneNumber', label: '휴대폰 번호를 입력해주세요', createOnly: true },
    { name: 'password', label: '비밀번호를 입력해주세요', createOnly: true },
    { name: 'verifyPassword', label: '비밀번호 확인을 입력해주세요', createOnly: true },
    { name: 'gender', label: '성별을 선택해주세요', createOnly: true },
    { name: 'classification', label: '재직구분을 선택해주세요' },
    { name: 'hiredDate', label: '입사일을 입력해주세요' },
    { name: 'responsibilities', label: '담당업무를 선택해주세요', isArray: true },
    { name: 'sportTypes', label: '종목을 선택해주세요', isArray: true },
  ];

  const focusField = fieldName => {
    // input, select, textarea 순서로 찾아서 포커스
    const el = document.querySelector(`[name="${fieldName}"]`)
      || document.querySelector(`input[name="${fieldName}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus();
    }
  };

  const submit = async values => {
    // 생성 모드: 아이디/전화번호 중복검사 필수
    if (!isEditMode && !usernameChecked) {
      toast.error('아이디 중복검사를 해주세요');
      focusField('username');
      return;
    }
    if (!isEditMode && !phoneChecked) {
      toast.error('휴대폰 번호 중복검사를 해주세요');
      focusField('phoneNumber');
      return;
    }

    // 필수값 검증 — 첫 번째 에러만 표시
    for (const field of REQUIRED_FIELDS) {
      if (field.createOnly && isEditMode) continue;
      const val = values[field.name];
      const isEmpty = field.isArray
        ? (!val || val.length === 0)
        : (!val || (typeof val === 'string' && val.trim() === ''));
      if (isEmpty) {
        setError(field.name, { type: 'manual', message: field.label });
        focusField(field.name);
        toast.error(field.label);
        return;
      }
    }

    // 이메일 형식 체크
    if (values.email && values.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      toast.error('올바른 이메일 형식으로 입력해주세요');
      focusField('email');
      return;
    }

    // 휴대폰 형식 체크
    if (values.phoneNumber && !/^010-\d{4}-\d{4}$/.test(values.phoneNumber)) {
      toast.error('올바른 휴대폰 번호 형식으로 입력해주세요 (010-0000-0000)');
      focusField('phoneNumber');
      return;
    }

    // 퇴사 시 퇴사일 필수
    if (values.classification === 'LEFT' && !values.leftDate) {
      setError('leftDate', { type: 'manual', message: '퇴사일을 입력해주세요' });
      focusField('leftDate');
      toast.error('퇴사일을 입력해주세요');
      return;
    }

    setLoader(true);
    if (values.leftDate && !validateDateRange(values.hiredDate, values.leftDate)) {
      toast.error('기간을 다시 확인해주세요');
      setLoader(false);
      return;
    }
    // 비밀번호 검증: 수정 모드에서 둘 다 비어있으면 변경 안 함(스킵)
    const hasPassword = values.password && values.password.trim();
    const hasVerify = values.verifyPassword && values.verifyPassword.trim();
    if (hasPassword || hasVerify) {
      if (hasPassword !== hasVerify || values.password !== values.verifyPassword) {
        setLoader(false);
        setError('verifyPassword', { type: 'manual', message: '비밀번호가 일치하지 않습니다' });
        focusField('verifyPassword');
        toast.error('비밀번호가 일치하지 않습니다');
        return;
      }
    }

    const formData = new FormData();
    if (previewImage) formData.append('profileImage', previewImage);
    formData.append('name', values.name || '');
    formData.append('branchId', userBranch?.branchId);
    formData.append('username', values.username || '');
    formData.append('phoneNumber', values.phoneNumber || '');
    if (values.birthDate && dayjs(values.birthDate).isValid()) formData.append('birthDate', dayjs(values.birthDate).format('YYYY-MM-DD'));
    if (values.email) formData.append('email', values.email);
    formData.append('gender', values.gender || 'MALE');
    formData.append('introduction', values.introduction || '');
    formData.append('responsibilities', values.responsibilities || []);
    formData.append('sportTypes', values.sportTypes || []);
    formData.append('employeeClassification', values.classification || 'WORKING');
    formData.append('employeeLoginYn', values.employeeLoginYn || 'N');
    formData.append('managerAppLoginYn', values.managerAppLoginYn || 'N');
    if (values.employeePosition && values.employeePosition !== '') formData.append('employeePositionId', values.employeePosition);
    if (values.hiredDate && dayjs(values.hiredDate).isValid()) formData.append('hiredDate', dayjs(values.hiredDate).format('YYYY-MM-DD'));
    formData.append('leftDate', values.leftDate ? dayjs(values.leftDate).format('YYYY-MM-DD') : '');
    if (values.password && values.password.trim()) formData.append('password', values.password);
    if (values.lessonFee) formData.append('lessonFee', values.lessonFee);
    if (values.contractWeeklyHours) formData.append('contractWeeklyHours', values.contractWeeklyHours);
    if (isEditMode && Array.isArray(employ?.accessGroupCodes)) formData.append('accessGroupCodes', values.accessGroupCodes ?? []);
    if (isEditMode && typeof employ?.adminAccess === 'boolean') formData.append('adminAccess', values.adminAccess ?? false);

    try {
      if (isEditMode) {
        await updateStaff(formData, user.token, id);
        toast.success(t('Employee information has been modified'));
        setPreviewImage('');
        queryClient.invalidateQueries({ queryKey: ['staff', id] });
      } else {
        await createStaff(formData, user.token);
        toast.success(t('Employees have been registered'));
      }
      // 목록 캐시 무효화
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      queryClient.invalidateQueries({ queryKey: ['employeesStats'] });
      if (!isEditMode) navigate('/employee');
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (isEditMode && employ) {
      reset({
        ...employ,
        employeePosition: employ.employeePositionId || '',
        accessGroupCodes: employ.accessGroupCodes ?? [],
        adminAccess: employ.adminAccess ?? false,
        contractWeeklyHours: employ.contractWeeklyHours ?? '',
        password: '',
        verifyPassword: '',
      });
    } else if (!isEditMode) {
      reset({
        name: '',
        username: '',
        phoneNumber: '',
        password: '',
        verifyPassword: '',
        birthDate: null,
        email: '',
        gender: 'MALE',
        introduction: '',
        responsibilities: [],
        sportTypes: [],
        classification: 'WORKING',
        employeeLoginYn: 'N',
        managerAppLoginYn: 'N',
        hiredDate: dayjs().format('YYYY-MM-DD'),
        leftDate: null,
        employeePosition: '',
        lessonFee: '',
        contractWeeklyHours: '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employ, isEditMode]);

  // ============================================
  // 좌측: 프로필 + 기본정보
  // ============================================
  const leftCard = (
    <LeftCard>
      <ProfileHeader>
        <PhotoWrap>
          <Photo src={getProfileImageSrc()} alt="avatar" />
          {/* 프로필 사진 업로드 기능 제거
          <CameraBtn htmlFor="imageupload">
            <PhotoCameraIcon sx={{ fontSize: '16px', color: '#4ab3bc' }} />
          </CameraBtn>
          <input type="file" id="imageupload" style={{ display: 'none' }} onChange={handleUploadImage} />
          */}
        </PhotoWrap>
        <Box>
          <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#fff' }}>
            {watchName || (isEditMode ? employ?.name : '새 임직원')}
          </Typography>
          <Typography sx={{ fontSize: '14px', color: 'rgba(255,255,255,0.75)', mt: '2px' }}>
            {userBranch?.branchName || '소속지점'}
          </Typography>
          {employ?.faceRegistered && (
            <Box sx={{ display: 'inline-flex', mt: '8px', px: '10px', py: '3px', borderRadius: '6px', backgroundColor: 'rgba(255,255,255,0.2)', fontSize: '12px', color: '#fff', fontWeight: 500 }}>
              안면등록완료
            </Box>
          )}
        </Box>
      </ProfileHeader>

      <FormBody>
        {/* 브라우저 자동완성 방지용 더미 필드 — 높이 0으로 DOM에 존재하되 보이지 않게 */}
        <Box sx={{ overflow: 'hidden', height: 0, width: 0, position: 'absolute' }}>
          <input type="text" name="fake_user" tabIndex={-1} autoComplete="username" />
          <input type="password" name="fake_pass" tabIndex={-1} autoComplete="new-password" />
        </Box>

        <FieldGroup>
          <FieldLabel>이름(한글) {!isEditMode && <RequiredMark>*</RequiredMark>}</FieldLabel>
          <FormInputText control={control} rules={isEditMode ? {} : { required: true }} name="name" tabIndex={1} autoComplete="off" inputProps={{ autoComplete: 'off' }} sx={INPUT_SX} placeholder="이름을 입력해주세요" />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>아이디 {!isEditMode && <RequiredMark>*</RequiredMark>}</FieldLabel>
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <FormInputText
              control={control} name="username" tabIndex={2} disabled={!!employ?.username}
              rules={isEditMode ? {} : { required: true, pattern: { value: NO_KOREAN_PATTERN, message: '아이디에 한글은 사용할 수 없습니다' } }}
              autoComplete="one-time-code" inputProps={{ autoComplete: 'one-time-code' }}
              onChange={e => { setUsernameChecked(false); if (!isEditMode && e.target.value) trigger('username'); else clearErrors('username'); }}
              sx={{ ...INPUT_SX, flex: 1 }} placeholder="아이디를 입력해주세요"
            />
            {!isEditMode && (
              <Button
                variant="contained" disabled={usernameChecking || usernameChecked}
                onClick={handleCheckUsername}
                sx={{
                  minWidth: '80px', height: '40px', borderRadius: '10px', fontSize: '13px', fontWeight: 600,
                  textTransform: 'none', flexShrink: 0,
                  backgroundColor: usernameChecked ? '#d4d4d4' : '#4ab3bc',
                  color: usernameChecked ? '#737373' : '#fff',
                  '&:hover': { backgroundColor: usernameChecked ? '#d4d4d4' : '#3a9da5' },
                  '&.Mui-disabled': { backgroundColor: '#d4d4d4', color: '#737373' },
                }}
              >
                {usernameChecking ? '확인중...' : usernameChecked ? '확인완료' : '중복확인'}
              </Button>
            )}
          </Box>
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>휴대폰 번호 {!isEditMode && <RequiredMark>*</RequiredMark>}</FieldLabel>
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <FormInputPhone
              control={control} setValue={setValue} tabIndex={3}
              rules={isEditMode ? {} : { required: true }}
              name="phoneNumber" autoComplete="off" inputProps={{ autoComplete: 'off' }}
              sx={{ ...INPUT_SX, bgcolor: 'transparent', flex: 1 }}
              placeholder="휴대폰 번호를 입력해주세요"
              onChange={() => setPhoneChecked(false)}
            />
            {!isEditMode && (
              <Button
                variant="contained"
                disabled={phoneChecking || phoneChecked}
                onClick={handleCheckPhone}
                sx={{
                  minWidth: '80px', height: '40px', borderRadius: '10px', fontSize: '13px', fontWeight: 600,
                  textTransform: 'none', flexShrink: 0,
                  backgroundColor: phoneChecked ? '#d4d4d4' : '#4ab3bc',
                  color: phoneChecked ? '#737373' : '#fff',
                  '&:hover': { backgroundColor: phoneChecked ? '#d4d4d4' : '#3a9da5' },
                  '&.Mui-disabled': { backgroundColor: '#d4d4d4', color: '#737373' },
                }}
              >
                {phoneChecking ? '확인중...' : phoneChecked ? '확인완료' : '중복확인'}
              </Button>
            )}
          </Box>
          {phoneError && <HintText>{phoneError}</HintText>}
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>비밀번호 {!isEditMode && <RequiredMark>*</RequiredMark>}</FieldLabel>
          <FormInputText
            control={control} type="password" tabIndex={4} autoComplete="one-time-code" inputProps={{ autoComplete: 'one-time-code' }} name="password" sx={INPUT_SX}
            placeholder={isEditMode ? '변경 시에만 입력' : '영문, 숫자, 특수문자 포함 8~16자'}
            rules={{
              ...(isEditMode ? {} : { required: true }),
              validate: value => {
                if (!value || value.trim() === '') return true;
                return /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*-])[A-Za-z\d!@#$%^&*-]{8,16}$/.test(value) || '영문, 숫자, 특수문자를 포함해 8~16자로 입력해주세요';
              },
            }}
          />
          {passwordError && <HintText>{passwordError}</HintText>}
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>비밀번호 확인 {!isEditMode && <RequiredMark>*</RequiredMark>}</FieldLabel>
          <FormInputText
            control={control} name="verifyPassword" tabIndex={5} autoComplete="one-time-code" inputProps={{ autoComplete: 'one-time-code' }} type="password" sx={INPUT_SX}
            placeholder={isEditMode ? '변경 시에만 입력' : '비밀번호를 다시 입력해주세요'}
            rules={isEditMode ? {} : { required: '비밀번호가 일치하지 않습니다', validate: value => value === getValues('password') || '비밀번호가 일치하지 않습니다' }}
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>성별 {!isEditMode && <RequiredMark>*</RequiredMark>}</FieldLabel>
          <FormInputRadio name="gender" control={control} options={gender} row sx={RADIO_TEAL} />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>생년월일</FieldLabel>
          <FormInputDate name="birthDate" control={control} placeholder="YYYY/MM/DD" renderInput={params => (
            <TextField color="info" size="small" tabIndex={6} variant="outlined" {...params} sx={DATE_INPUT_SX} inputProps={{ ...params.inputProps, autoComplete: 'off' }} />
          )} />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>이메일</FieldLabel>
          <FormInputText control={control} name="email" type="email" autoComplete="off" inputProps={{ autoComplete: 'off' }} placeholder="이메일을 입력해주세요" sx={INPUT_SX} />
          {emailError && <HintText>{emailError}</HintText>}
        </FieldGroup>

        {/* 소개글 — 추후 활성화 */}
        {/* <FieldGroup>
          <FieldLabel>소개글</FieldLabel>
          <FormInputTextArea
            control={control} minRows={3} name="introduction" placeholder="강사 소개글을 입력해주세요"
            style={{ borderRadius: '10px', width: '100%', color: '#333', padding: '12px', fontSize: '14px', fontWeight: 500, border: '1px solid #e5e5e5', background: '#fafafa' }}
          />
        </FieldGroup> */}
      </FormBody>
    </LeftCard>
  );

  // ============================================
  // 우측: 인사정보
  // ============================================
  const rightPanel = (
    <RightPanel>
      {/* 업무 & 종목 */}
      <Section>
        <SectionTitle>업무 및 종목</SectionTitle>
        <Row>
          <RowLabel>담당업무 <RequiredMark>*</RequiredMark></RowLabel>
          <RowContent><RHFCheckbox control={control} row name="responsibilities" spacing={2} options={Respo} /></RowContent>
        </Row>
        <Row>
          <RowLabel>종목 <RequiredMark>*</RequiredMark></RowLabel>
          <RowContent>
            <AllCheckboxLabel
              control={<StyledCheckbox checked={!!(watchSportTypes && watchSportTypes.length === sportType.length)} onChange={() => handleAllCheck('sportTypes')} icon={<img src="/images/icons/unchecked.svg" alt="" />} checkedIcon={<img src="/images/icons/checked.svg" alt="" />} />}
              label="전체"
            />
            <FilterDivider />
            <RHFCheckbox
              control={control} row name="sportTypes" spacing={0} options={sportType}
              sx={{ '& .MuiFormControlLabel-root': { width: '100px', mr: '24px' } }}
              rules={{ required: '종목을 하나 이상 선택해주세요', validate: value => (!value || value.length === 0) ? '종목을 하나 이상 선택해주세요' : true }}
            />
          </RowContent>
        </Row>
      </Section>

      {/* 근무 설정 */}
      <Section>
        <SectionTitle>근무 설정</SectionTitle>
        <Row>
          <RowLabel>재직구분 <RequiredMark>*</RequiredMark></RowLabel>
          <RowContent><FormInputRadio name="classification" rules={{ required: true }} control={control} options={employeeClassification} row sx={RADIO_TEAL} /></RowContent>
        </Row>
        <Row>
          <RowLabel>입사일 <RequiredMark>*</RequiredMark></RowLabel>
          <RowContent>
            <Box sx={{ width: '280px' }}>
              <FormInputDate name="hiredDate" control={control} placeholder="YYYY/MM/DD" renderInput={params => (
                <TextField color="info" size="small" variant="outlined" {...params} sx={{ ...DATE_INPUT_SX, width: '280px' }} />
              )} />
            </Box>
          </RowContent>
        </Row>
        {watchClassification === 'LEFT' && (
          <Row>
            <RowLabel>퇴사일 <RequiredMark>*</RequiredMark></RowLabel>
            <RowContent>
              <Box sx={{ width: '280px' }}>
                <FormInputDate name="leftDate" control={control} placeholder="YYYY/MM/DD" minDate={watchHiredDate || null} renderInput={params => (
                  <TextField color="info" size="small" variant="outlined" {...params} sx={{ ...DATE_INPUT_SX, width: '280px' }} />
                )} />
              </Box>
            </RowContent>
          </Row>
        )}
        <Row>
          <RowLabel>직급</RowLabel>
          <RowContent>
            <FormInputDropdown name="employeePosition" control={control} options={positions} staticValue=""
              sx={{ width: '280px', '& .MuiSelect-select': { backgroundColor: '#fafafa', borderRadius: '10px' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e5e5' } }}
            />
          </RowContent>
        </Row>
        <Row>
          <RowLabel>주 소정근로시간</RowLabel>
          <RowContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '280px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FormInputText name="contractWeeklyHours" control={control} type="text" placeholder="예: 40"
                  onChange={e => {
                    const v = e.target.value;
                    if (WEEKLY_HOURS_PATTERN.test(v)) setValue('contractWeeklyHours', v);
                  }}
                  sx={{ flex: 1, ...INPUT_SX }}
                />
                <Typography sx={{ fontSize: '14px', color: '#737373', flexShrink: 0 }}>시간</Typography>
              </Box>
              {monthlyHoursGuide && <HelperText>{monthlyHoursGuide}</HelperText>}
            </Box>
          </RowContent>
        </Row>
      </Section>

      {/* 로그인 설정 */}
      <Section>
        <SectionTitle>로그인 설정</SectionTitle>
        <Row>
          <RowLabel>매니저앱 로그인</RowLabel>
          <RowContent>
            <Controller name="managerAppLoginYn" control={control} rules={{ required: true }}
              render={({ field }) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <TealSwitch checked={field.value === 'Y'} onChange={e => field.onChange(e.target.checked ? 'Y' : 'N')} />
                  <Typography sx={{ fontSize: '14px', color: field.value === 'Y' ? '#4ab3bc' : '#a3a3a3', fontWeight: 500 }}>
                    {field.value === 'Y' ? '허용' : '비허용'}
                  </Typography>
                </Box>
              )}
            />
          </RowContent>
        </Row>
      </Section>

      {/* 출입 관리 (수정 모드 전용) */}
      {isEditMode && (
        <Section>
          <SectionTitle>출입 관리</SectionTitle>
          <Row>
            <RowLabel>출입가능구역</RowLabel>
            <RowContent>
              {accessGroupsQuery.isSuccess && accessGroupOptions.length === 0
                ? <Typography sx={{ fontSize: '14px', color: '#a3a3a3' }}>등록된 출입그룹이 없습니다</Typography>
                : <RHFCheckbox control={control} row name="accessGroupCodes" spacing={2} options={accessGroupOptions} />
              }
            </RowContent>
          </Row>
          <Row>
            <RowLabel>관리자 출입</RowLabel>
            <RowContent>
              <Controller name="adminAccess" control={control} defaultValue={false}
                render={({ field }) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <TealSwitch checked={!!field.value} onChange={e => field.onChange(e.target.checked)} />
                    <Typography sx={{ fontSize: '14px', color: field.value ? '#4ab3bc' : '#a3a3a3', fontWeight: 500 }}>
                      24시간 전 구역
                    </Typography>
                  </Box>
                )}
              />
            </RowContent>
          </Row>
        </Section>
      )}

      <Footer>
        <CancelBtn onClick={() => navigate(-1)}>취소</CancelBtn>
        <SubmitBtn disabled={loader} onClick={() => submit(getValues())}>저장</SubmitBtn>
      </Footer>
    </RightPanel>
  );

  // ============================================
  // 렌더링
  // ============================================
  const layout = (
    <Box>
      {!isEditMode && (
        <BackLink to="/employee">
          <ArrowBackIcon sx={{ fontSize: 20 }} />
          <Typography sx={{ fontSize: '18px', fontWeight: 700 }}>임직원 등록</Typography>
        </BackLink>
      )}
      <PageWrapper>
        {leftCard}
        {rightPanel}
      </PageWrapper>
    </Box>
  );

  if (!isEditMode) return <AnimationPage><Box sx={{ py: 2 }}>{layout}</Box></AnimationPage>;
  return <Box sx={{ py: 1 }}>{layout}</Box>;
}

export default EmployeeTab1;
